import { type CHCameraId, type DeviceId, type EventId, type SiteId } from '@solinkcorp/api-types';
import { type EventStandard } from '@solinkcorp/event-standard';
import { type EstimatedEvent } from '@solinkcorp/motion-events-estimator';
import {
  type ElasticSearchQuery,
  type IHistogramApiParams,
  type IRankingResult,
} from '@solinkcorp/vd-common';
import { type AggregationResult } from 'components/DiscoverPage/types';
import _chunk from 'lodash/chunk';
import AnalyticsLogger from 'models/AnalyticsLogger';
import { type EventType } from 'src/types';
import { HIDE_FROM_DISPLAY_TAG, type PushToCloudStatus } from 'vd-constants';
import { type AbstractRequestor } from '../shared/AbstractRequestor';
import AppCache from '../shared/AppCache';
import { normalizeEvent } from '../shared/eventUtils';
import { addHardcodedFieldStats } from '../shared/fieldstatsUtils';
import { sourceHeaders } from '../shared/source';

/**
 * Max concurrent `GET /events/{id}` requests issued by `getEventsByIds`. Bounds
 * in-flight fetches when resolving a large event-id set one document at a time.
 */
export const GET_EVENTS_BY_IDS_CONCURRENCY = 20;

export interface VideoLinkItem {
  /**
   * the time the link item is added, this can be the completion of an async process
   * such as cloud AI verification
   */
  timestamp?: number;
  title?: string;
  cameraId?: string;
  metadata?: IEventMedata;
  type?: 'AGENT_OUTPUT' | 'AGENT_ACTION' | 'ALARM_EVENT' | 'ALARM_OPENED';
}

// See https://solinkplatform.docs.apiary.io/#reference/events/event-endpoint,
// and https://github.com/SolinkCorp/platform-api/blob/master/src/DataAccessObjects/Event.cs.
export interface IEvent extends Omit<
  EventStandard,
  | 'startTime'
  | 'endTime'
  | 'startTimeLocal'
  | 'endTimeLocal'
  | 'pushToCloudStatus'
  | 'cameras'
  | 'shares'
  | 'ai'
  | 'videoLinkItems'
> {
  // There will always be an ID here by the time we get this.
  id: EventId;
  // The auth0 user id of the user who created this event.
  creator: string;
  createTime: string;
  // These are strings in the JSON we get back from platform API, but we convert
  // them to real dates below in `normalizeEvent()`.
  startTime: Date;
  endTime: Date;
  startTimeLocal: Date;
  endTimeLocal: Date;
  // From https://github.com/SolinkCorp/platform-api/blob/master/src/DataAccessObjects/Camera.cs
  cameras: EventCamera[];
  // From https://github.com/SolinkCorp/platform-api/blob/master/src/DataAccessObjects/VideoClip.cs
  videos: {
    /** URL of the video. */
    url: string;
    /** The start time of the video milliseconds from epoch. */
    startTime?: number;
    /** The end time of the video milliseconds from epoch. */
    endTime?: number;
    /** The stream name the video clip was recorded from (e.g. "SD") */
    streamName: string;
    /** Value used to redownload the video clip. */
    video_params: string;
    /** Camera id of the video source. */
    cameraId: string;
    status: 'inProgress' | 'complete' | 'error' | 'cancelled';
    /** Number of bytes uploaded. */
    uploaded?: number;
    /** Total size in bytes. */
    totalSize?: number;
  }[];
  downloads?: {
    streamId: string;
    expiration?: number;
    downloaded: { email: string; downloadedAt: number[] }[];
  }[];
  pushToCloudStatus?: PushToCloudStatus;
  restrictions?: {
    fillGaps?: boolean;
    faceblur?: boolean;
  };
  ai?: EventStandard['ai'] & { dwell_time: boolean };
  shares?: Share[];
  videoLinkItems?: VideoLinkItem[];
  shareSize?: number;
  shareDuration?: number;
  locationId: DeviceId;
  siteId: SiteId;
}
export interface VDEstimatedEvent extends EstimatedEvent {
  locationId: DeviceId;
  siteId: SiteId;
}

export interface IEventMedata {
  [key: string]: any;
  peopleDetected?: number;
  vehicleDetected?: number;
  /** device timestamp for the thumbnail analyzed by rekognition.  */
  detectionTimestamp?: number;
  /** True if the link item describes an error, e.g Spot Check preview error */
  isError?: boolean;
  summary?: string;
  reasoning?: string;
  additionalDetails?: {
    title?: string;
    description?: string;
    subject?: string;
    date?: string;
    notificationRecipients?: Array<{ email?: string; phoneNumber?: string }>;
  };
}

export interface FutureEventEntity extends IEvent {
  title: string;
  subtype: string;
  tags: ('hideFromDisplay' | 'excludeFromCount')[];
  details: {
    externalId: string;
    scheduledActivity: {
      title: string;
      description?: string;
      reason: string;
      alarmEnable: boolean;
      count: number;
      duration: number;
      period: {
        start: string;
        end: string;
      };
    };
  };
}
export interface Share {
  sharedBy: string;
  email: string;
  sms?: string;
  notified?: number;
  views?: number[];
  downloads?: number[];
  sharedAt?: number;
  expiration?: number;
  message?: string;
  id?: string;
  status?: 'open' | 'closed';
  restrictions?: {
    downloads?: boolean;
    audio?: boolean;
  };
}

export interface EventCameraStream {
  id: string;
  name: string;
  earliestSegmentDate?: number;
  latestSegmentDate?: number;
  totalSegments?: number;
}

export interface EventCamera {
  id: string;
  // `chId` is sometimes missing - e.g. saved clips created from iOS.`
  chId?: CHCameraId;
  name: string;
  /** Http url to a camera thumbnail. */
  thumbnail: string;
  snapshot_params?: string;
  streams: EventCameraStream[];
  annotations?: {
    label: string;
    type: string;
    subtype: string;
    points: [number, number][];
  }[];
  /** Array of additional images from the camera. */
  images?: {
    url: string;
    date: number;
    /** Value used to redownload the image from the device. */
    snapshot_params: string;
    totalSize: number;
    uploaded: number;
    status: 'inProgress' | 'complete' | 'error' | 'cancelled';
  }[];
  shares?: Share[];
}

// From https://github.com/SolinkCorp/platform-api/blob/master/src/SolinkCloudService/ApiRequestObjects/EventQueryRequest.cs
export interface EventQueryParameters {
  /** A filter id from /Filters endpoint, may be combined with q. */
  filterId?: string;
  reportTemplateId?: string;
  /** The number of events to skip. */
  offset?: number;
  /** The number of events to return. */
  count?: number;
  sort?: string;
  sorts?: { [key: string]: { order: 'asc' | 'desc' } }[];
  /** The type of event to filter by. Optionally accepts comma separated list of types as input. */
  type?: string;
  start?: string;
  end?: string;
  /** The time field start and end time will be used with. */
  timeFieldToSearchOn?: string;
  /**
   * If true (or not specified), we will fetch all events that overlap the specified
   * time range.  If false, we'll strictly fetch events where the startTime is between
   * the start and end.
   */
  fullRange?: boolean;
  /**
   * If true, the returned total will be the actual total event count.  If false
   * (the default) then the returned total will be capped at 10,000.
   */
  totalLimit?: boolean;
  must?: readonly any[];
  // Note: This needs to be an array, but we define this as `any[] | any` to get around
  // this unfortunately problem in chai's types: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/9120
  should?: readonly any[] | any;
  must_not?: readonly any[];
  filter?: readonly any[];
  /** Return a scroll id in the response to use with scroll endpoint. */
  scroll?: string;
  minimum_should_match?: number;
  rawAggregations?: any;
  /** A list of strings that represent the event data fields to return (or exclude if value is prepended with '!'). */
  fields?: readonly string[];
  sortOrder?: 'asc' | 'desc';
  include?: string;
  next?: unknown[];
  /** A unique string which identifies where in the code we're fetching events from. */
  source?: string;
}

// TODO: Not sure where this enum comes from, but I'm guessing these are all the values in it.  :)
export type AggType = 'Count' | 'Min' | 'Max' | 'Average' | 'Sum';

// From https://github.com/SolinkCorp/platform-api/blob/master/src/DataAccessManager/SearchObjects/LegacyGroupBy.cs#L34
export interface SubStatsAgg {
  Count?: number;
  Min?: number;
  Max?: number;
  Average?: number;
  Sum?: number;
  Meta: {
    aggType: AggType;
  };
}

export interface FieldStat {
  docCount: number;
  name: string;
  type: string | 'string' | 'boolean' | 'float' | 'long' | 'short' | 'date' | 'integer';
}

export interface SingleEventFieldStat extends FieldStat {
  terms: string[];
}

export interface AlarmStats {
  [key: string]: { OPEN: number; CLOSED: number };
}

// Returns true if the passed in date
function isValidDateString(dateStr: string): boolean {
  return !isNaN(Date.parse(dateStr));
}

export interface ListHistogramResult {
  total: number;
  offset: number;
  count: number;
  results: {
    startTime: string;
    endTime: string;
    total: number;
    results: {
      type: EventType;
      count: number;
      totalAmountSum: number;
      fieldStats: unknown;
    }[];
  }[];
  summary: {
    results: {
      type: EventType;
      count: number;
      fieldStats: {
        [key: string]:
          | {
              sum?: number;
              avg?: number;
              count?: number;
              percentage?: number;
              ratio?: number;
            }
          | undefined;
      };
    }[];
  };
}

export interface PlatformEventAggsData {
  Aggs: {
    groupBy: {
      DocCountErrorUpperBound: number;
      Items: AggregationResult[];
      SumOtherDocCount: number;
    };
  };
  count: number;
  results: [];
  total: number;
}

type ShareForArchival = Pick<Share, 'id' | 'status'>;
export interface EventForArchival {
  id: string;
  shares: ShareForArchival[] | undefined;
}

export interface ClipsStorageAggregationByLocation {
  Aggs: {
    sites: {
      doc_count_error_upper_bound: number;
      sum_other_doc_count: number;
      buckets: {
        key: string;
        doc_count: number;
        shareSize: {
          value: number;
          value_as_string: string;
        };
      }[];
    };
  };
  total: number;
  count: number;
  results: unknown[];
}

export type FieldStatsContext = 'login' | `events:${string}` | 'clips';

/**
 * Leading canonical UUID (8-4-4-4-12 hex) followed by a `-` separator — the
 * `{cloudId}` prefix the NDE worksheet prepends to build its composite event id.
 */
const COMPOSITE_CLOUD_ID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;

/**
 * Strip the `{cloudId}-` prefix off an NDE worksheet composite event id to get
 * the plain eventId that Elasticsearch indexes as its `id` field.
 *
 * The worksheet emits `{cloudId}-{eventId}` (e.g.
 * `95f70eb0-fad0-11eb-b751-5797cc7d0730-229082998857672-1783270780000`).
 * `GET /events/{id}` resolves the composite, but the ES-backed
 * `POST /events/query` `terms: { id }` filter is keyed on the plain eventId
 * (here `229082998857672-1783270780000`).
 *
 * Pass `cloudId` whenever the input MAY already be a plain id. The plain eventId
 * is `{deviceId}-{startTimeMs}` (or a `V2__…` transaction id) and `deviceId` can
 * itself be a UUID, so a shape-based strip of "any leading UUID" would truncate a
 * real plain id and collapse two distinct events. An exact `{cloudId}-` strip is
 * unambiguous — a plain id never begins with the tenant cloudId, so it is
 * returned unchanged.
 *
 * When `cloudId` is omitted this falls back to stripping the fixed-shape leading
 * UUID. That fallback is only safe for callers that KNOW the id is composite (the
 * leading UUID is then always the cloudId); never rely on it for ids that may be
 * plain.
 */
export function toPlainEventId(id: string, cloudId?: string): string {
  if (cloudId) {
    // Exact-prefix strip only. A plain id never starts with the tenant cloudId,
    // so it is returned unchanged — crucially, we do NOT fall through to the
    // shape strip below, which would truncate a plain id whose deviceId is a UUID.
    return id.startsWith(`${cloudId}-`) ? id.slice(cloudId.length + 1) : id;
  }
  // No cloudId: the caller guarantees a composite id, so the fixed-shape leading
  // UUID is always the cloudId and is safe to strip.
  return id.replace(COMPOSITE_CLOUD_ID_PREFIX, '');
}

export class PlatformApi {
  constructor(private readonly requestor: AbstractRequestor) {}

  async listEvents(params: EventQueryParameters): Promise<{
    total: number;
    next?: unknown[];
    offset?: number;
    count: number;
    results: IEvent[];
  }> {
    if (params.start !== undefined && params.start !== null && !isValidDateString(params.start)) {
      throw new Error(`Invalid start: ${params.start}`);
    }

    if (params.end !== undefined && params.end !== null && !isValidDateString(params.end)) {
      throw new Error(`Invalid end: ${params.end}`);
    }

    const response: any = await this.requestor.post<unknown>({
      url: '/events/query',
      data: params,
      headers: sourceHeaders(params.source),
    });
    if (response.results && response.results.length > 0) {
      response.results.forEach((event: any) => {
        normalizeEvent(event);
      });
    }
    return response;
  }

  async getEvent(id: string): Promise<IEvent> {
    const event: any = await this.requestor.get<any>({
      url: `/events/${id}`,
    });
    return normalizeEvent(event);
  }

  getEventAggregations(query: { query: ElasticSearchQuery; aggs: any }) {
    return this.requestor.post<PlatformEventAggsData>({
      url: '/events/query',
      data: {
        count: 0,
        must: query.query.toFilter().bool.must,
        must_not: query.query.toFilter().bool.must_not,
        rawAggregations: query.aggs,
      },
      headers: sourceHeaders('#getEventAggregations'),
    });
  }

  async topHistogram(params: unknown) {
    try {
      return this.requestor.post<IRankingResult[]>({
        url: '/events/top3',
        data: params,
      });
    } catch (err) {
      console.error('Error calling Top3 Histogram', err);
      throw err;
    }
  }

  async listHistogram(params: IHistogramApiParams): Promise<ListHistogramResult> {
    const { expires, ...data } = params;
    try {
      return this.requestor.post<ListHistogramResult>({
        url: '/events/histogram/query',
        data,
        expires,
      });
    } catch (err) {
      console.error('Error getting histogram', err);
      throw err;
    }
  }

  async eventFieldStats(options: {
    queryData?: EventQueryParameters;
    timeout?: number;
    isQuick?: boolean;
    context: FieldStatsContext;
  }): Promise<FieldStat[]> {
    const requestStart = performance.now();
    const res: any = await this.requestor.request<unknown>({
      method: 'POST',
      url: `/events/fieldstats${options?.isQuick ? '?quick=true' : ''}`,
      options: {
        data: options?.queryData ?? {},
        timeout: options?.timeout,
      },
    });

    AnalyticsLogger.fieldStatsLoadTime({
      timeInSeconds: Number(((performance.now() - requestStart) / 1000).toFixed(3)),
      context: options?.context,
      isQuick: !!options?.isQuick,
    });

    const fieldStats: FieldStat[] = res.data;
    addHardcodedFieldStats(fieldStats);

    return fieldStats;
  }

  singleEventFieldStats(query: string, start: unknown, end: unknown, locationIds?: string[]) {
    const data: { start: any; end: any; must?: any; include: string } = {
      start,
      end,
      include: HIDE_FROM_DISPLAY_TAG,
    };
    if (locationIds?.length) {
      data.must = [{ terms: { siteId: locationIds } }];
    }
    return this.requestor.post<SingleEventFieldStat>({
      url: `/events/fieldstats/${query}`,
      data,
    });
  }

  async createReport({
    userId,
    customerId,
    reportTemplateId,
    name,
    description,
    query,
    locale = 'en-CA',
    reportMetadata,
  }: ICreateReportParams) {
    const data: any = {
      query,
      userId,
      customerId,
      reportTemplateId,
      name: { [locale]: name },
      reportType: 'filter',
      queryType: 'raw',
      reportMetadata,
    };

    if (description) {
      data.description = { [locale]: description };
    }

    try {
      const result: any = await this.requestor.post<any>({ url: '/reporttemplates', data });
      // Handle case where Platform returns success, even though Callhome creation failed (returned as error in response)
      if (result.error) {
        throw new Error(result.error.message);
      }
      // Invalidate the cache for reports, since we just updated it
      AppCache.invalidate({ request: `/users/${userId}/reporttemplates` });
      return result;
    } catch (error) {
      console.error('Error creating customer report', error);
      throw error;
    }
  }

  async createEvent(event: any) {
    const cache: string[] = [];
    function removeCircular(key: string, value: any) {
      if (typeof value === 'object' && value !== null) {
        if (cache.indexOf(value) !== -1) {
          // Circular reference found, discard key
          return;
        }

        cache.push(value);
      }

      return value;
    }

    const newEvent = (await this.requestor.post<unknown>({
      url: '/events',
      data: JSON.parse(JSON.stringify(event, removeCircular)),
    })) as IEvent;
    return normalizeEvent(newEvent);
  }

  editEvent(eventId: string, payload: unknown) {
    return this.requestor.put<unknown>({
      url: `/events/${eventId}/edit`,
      data: payload,
    });
  }

  deleteEvent(eventId: string) {
    return this.requestor.delete<unknown>({ url: `/events/${eventId}` });
  }

  archiveEvent(eventId: string) {
    return this.requestor.post<unknown>({ url: `/events/${eventId}/archive`, data: {} });
  }

  bulkArchiveEvents(events: EventForArchival[]) {
    return this.requestor.post<unknown>({ url: `/events/archive/bulk`, data: { events } });
  }

  unarchiveEvent(eventId: string) {
    return this.requestor.post<unknown>({ url: `/events/${eventId}/unarchive`, data: {} });
  }

  createEventIngest(event: any, runSynchronously = false): unknown {
    return this.requestor.post<unknown>({
      url: `/events/ingest${runSynchronously ? '?runAsync=false' : ''}`,
      data: event,
    });
  }

  getClipsStorage(locationIds: string[]) {
    return this.requestor.post<ClipsStorageAggregationByLocation>({
      url: '/events/query',
      data: {
        count: 0,
        rawAggregations: {
          sites: {
            terms: {
              field: 'siteId',
              size: 10000,
            },
            aggs: {
              shareSize: {
                sum: {
                  field: 'shareSize',
                },
              },
              filter_by_share_size: {
                bucket_selector: {
                  buckets_path: {
                    shareSize: 'shareSize',
                  },
                  script: 'params.shareSize > 0',
                },
              },
            },
          },
        },
        filter: [
          {
            terms: {
              siteId: locationIds,
            },
          },
        ],
      },
    });
  }

  /**
   * Fetch full events by their IDs.
   *
   * Resolves each id via the `GET /events/{id}` path-param endpoint (the same
   * lookup `getEvent`/`useViewEvent.viewEventById` uses to open the video
   * player). The event ids produced by the NDE event-grain worksheet are the
   * composite `{cloudId}-{eventId}` identifier, which `GET /events/{id}`
   * resolves correctly — a `/events/query` `terms: { id }` filter does NOT
   * match it (the indexed ES `id` field is not the composite), so the previous
   * bulk-query approach silently returned zero events.
   *
   * Requests are issued in bounded-concurrency batches. Ids with no matching
   * event (or a failed fetch) are omitted, not errored, and the result
   * preserves the input order.
   */
  async getEventsByIds(ids: string[]): Promise<IEvent[]> {
    // Short-circuit: no IDs means no request.
    if (ids.length === 0) {
      return [];
    }

    // Cap in-flight requests so a large id set doesn't fire hundreds of GETs at
    // once; batches run sequentially, ids within a batch concurrently.
    const batches: string[][] = _chunk(ids, GET_EVENTS_BY_IDS_CONCURRENCY);
    const results: (IEvent | undefined)[] = [];
    for (const batch of batches) {
      const batchResults = await Promise.all(
        batch.map((id) =>
          this.getEvent(id).catch(() => {
            // Missing/unresolvable id: omit it rather than failing the whole set.
            return undefined;
          }),
        ),
      );
      results.push(...batchResults);
    }

    // Promise.all preserves order within each batch and batches are pushed in
    // order, so `results` already mirrors `ids`; just drop the omitted ones.
    return results.filter((event): event is IEvent => event !== undefined);
  }

  /**
   * Like {@link getEventsByIds}, but ensures each event's cameras carry the
   * denormalized `thumbnail` / `images` fields needed to render video
   * thumbnails (e.g. in the NDE inline Events View cards).
   *
   * `GET /events/{id}` — the composite-safe lookup {@link getEventsByIds} uses
   * to resolve the worksheet's `{cloudId}-{eventId}` ids — returns the
   * authoritative event but *without* the denormalized `cameras[].thumbnail` /
   * `cameras[].images` that the Elasticsearch-backed `POST /events/query`
   * provides. Cards fed directly by the by-id path therefore render an empty
   * thumbnail even when the event has video, while every query-fed surface
   * (Events page grid, the video player's event column) shows thumbnails fine.
   *
   * This resolves the events by id (composite-safe), then re-fetches them by
   * their *plain* eventId (the composite's `{cloudId}-` prefix stripped via
   * {@link toPlainEventId}) through the query endpoint — which ES matches,
   * unlike the composite — and grafts the denormalized camera list onto each
   * event. Note the by-id response echoes the composite `id`, so the plain id
   * must be derived from it rather than read off the resolved event.
   *
   * Enrichment is best-effort: if the query fails or an event isn't matched
   * (e.g. not yet indexed in ES), that event is returned unchanged, so this
   * never regresses to the empty-events failure a bulk composite-id query hit.
   */
  async getEventsByIdsWithThumbnails(ids: string[]): Promise<IEvent[]> {
    const events = await this.getEventsByIds(ids);
    if (events.length === 0) {
      return events;
    }

    // Bound the `terms` query to the events' time span; the query endpoint
    // expects a start/end range. Guard against unparseable startTimes so a
    // single bad event can't collapse the range to NaN and throw.
    const times = events
      .map((event) => event.startTime?.getTime())
      .filter((time): time is number => typeof time === 'number' && !Number.isNaN(time));
    if (times.length === 0) {
      return events;
    }
    const start = new Date(Math.min(...times) - 60_000).toISOString();
    const end = new Date(Math.max(...times) + 60_000).toISOString();
    // The by-id events carry the composite `{cloudId}-{eventId}` id, but ES
    // indexes (and the query result `id` echoes) the plain eventId — so query by
    // the plain id and remember the composite→plain mapping to match results
    // back. Querying by the composite matches zero docs (the pre-fix no-op).
    const plainIdByCompositeId = new Map(
      events.map((event) => [event.id, toPlainEventId(event.id)]),
    );
    const plainIds = [...new Set(plainIdByCompositeId.values())];

    let enrichedByPlainId: Map<string, IEvent>;
    try {
      const { results } = await this.listEvents({
        start,
        end,
        count: plainIds.length,
        fullRange: true,
        must: [{ terms: { id: plainIds } }],
        source: 'getEventsByIdsWithThumbnails',
      });
      enrichedByPlainId = new Map(results.map((event) => [event.id, event]));
    } catch {
      // Best-effort: fall back to the un-enriched (by-id) events.
      return events;
    }

    return events.map((event) => {
      const enriched = enrichedByPlainId.get(plainIdByCompositeId.get(event.id) ?? event.id);
      // The query result is the same denormalized document the Events page
      // renders, so adopt its camera list wholesale (it carries the thumbnails)
      // while keeping the authoritative by-id event for everything else.
      if (!enriched || enriched.cameras.length === 0) {
        return event;
      }
      return { ...event, cameras: enriched.cameras };
    });
  }

  /**
   * Fetch events by their IDs in a single `POST /events/query` request.
   *
   * Uses the ES `ids` query to match document `_id`s directly — the same lookup
   * `GET /events/{id}` performs, but batched. Composite `{cloudId}-{eventId}`
   * inputs are normalised to the plain eventId via {@link toPlainEventId} before
   * querying, so the method is safe for both plain and composite callers.
   * Returns events in the same order as the input `ids`; ids with no match are
   * omitted.
   */
  async getEventsByIdsBatch(ids: string[], cloudId?: string): Promise<IEvent[]> {
    if (ids.length === 0) {
      return [];
    }

    // When cloudId is provided, callers may pass composite `{cloudId}-{eventId}`
    // ids. Normalise to the plain eventId that ES indexes as both `_id` and
    // `_source.id`. Without cloudId the regex fallback in toPlainEventId can
    // truncate plain ids whose deviceId is a UUID, so skip normalisation.
    const plainByInput = new Map(ids.map((id) => [id, cloudId ? toPlainEventId(id, cloudId) : id]));
    const uniquePlainIds = [...new Set(plainByInput.values())];

    const { results } = await this.listEvents({
      count: uniquePlainIds.length,
      must: [{ ids: { values: uniquePlainIds } }],
      source: 'getEventsByIdsBatch',
    });

    const resultByPlainId = new Map(results.map((event) => [event.id as string, event]));

    const ordered: IEvent[] = [];
    for (const id of ids) {
      const event = resultByPlainId.get(plainByInput.get(id) as string);
      if (event) {
        ordered.push(event);
      }
    }
    return ordered;
  }
}

interface ICreateReportParams {
  userId: string;
  customerId: string;
  reportTemplateId: string;
  name: string;
  description: string;
  query: unknown;
  locale: string;
  reportMetadata: {
    categories: string[];
    version: string;
  };
}
